import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { ok } from 'neverthrow';
import { v4 } from 'uuid';
import { ResourceType } from '@cotera/api';
import type { OpenArtifactRequest } from '@cotera/client/v0/chat/artifact-panel-context';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { navigateChatSeed } from '../chat-seed';
import { workflowChatSeedStrategy } from './chat-seed';

type Params = {
  /** When set, workflow will be assigned to this folder when first saved. Passed in nav state. */
  folderId?: string;
  /**
   * When set, the new workflow opens pre-bound to this existing trigger (e.g.
   * "Create new workflow" clicked under a trigger card) instead of blank.
   */
  seedTrigger?: {
    id: string;
    kind: string;
    name: string;
    schedule: string | null;
    eventName?: string | null;
  };
  /**
   * Whether to navigate into a fresh workflow chat. Defaults to true.
   *
   * Pass `false` from a caller already inside an artifact panel that wants the
   * workflow to land as a sibling tab beside what the user is looking at (the
   * workflow they're building, say) rather than navigating the whole app away
   * from it. Requires {@link openArtifact}; without it there is no panel to add
   * a tab to and this falls back to navigating.
   */
  newChat?: boolean;
  /**
   * The hosting panel's `openArtifact`, from `useArtifactPanel()`.
   *
   * Passed in rather than read here because the panel is React context whose
   * value depends on where the *caller* sits in the tree, and this action is
   * constructed once at app root with no position of its own.
   */
  openArtifact?: (request: OpenArtifactRequest) => void;
};

export class NewWorkflowAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New Workflow';
  description = 'Create a new workflow';
  inputSchema = z.object({
    folderId: z.string().uuid().optional(),
    seedTrigger: z
      .object({
        id: z.string(),
        kind: z.string(),
        name: z.string(),
        schedule: z.string().nullable(),
        eventName: z.string().nullable().optional(),
      })
      .optional(),
    newChat: z.boolean().optional(),
    openArtifact: z
      .custom<(request: OpenArtifactRequest) => void>(
        (value) => typeof value === 'function'
      )
      .optional(),
  });
  group = 'Workflow';
  icon = 'workflow';

  private _lastCreatedWorkflowId: string | null = null;
  private _lastCreatedChatId: string | null = null;

  constructor(
    private readonly navigate: (
      path: string,
      options?: { state?: unknown }
    ) => void,
    private readonly orgSlug: string
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const workflowId = v4();

    // The workflow is not persisted either way — it opens as a draft the user
    // builds and saves. In the panel the draft marker rides in the artifact
    // payload rather than a seed URL, which is what keeps the artifact the user
    // is already looking at open in the same panel.
    if (params.newChat === false && params.openArtifact !== undefined) {
      params.openArtifact({
        type: ResourceType.Workflow,
        artifactId: workflowId,
        payload: { workflowId, isNewDraft: true },
      });
      this._lastCreatedWorkflowId = workflowId;
      // No chat was created, and a stale id from a previous navigate would
      // report this workflow as belonging to someone else's conversation.
      this._lastCreatedChatId = null;
      return ok({ resourceId: workflowId });
    }

    const { chatId } = navigateChatSeed(
      workflowChatSeedStrategy,
      this.navigate,
      this.orgSlug,
      {
        artifactId: workflowId,
        isNew: true,
        ...(params.folderId !== undefined && { folderId: params.folderId }),
        ...(params.seedTrigger !== undefined && {
          seedTrigger: params.seedTrigger,
        }),
      }
    );
    this._lastCreatedWorkflowId = workflowId;
    this._lastCreatedChatId = chatId;
    return ok({ resourceId: workflowId });
  }

  track(): ActionTrackMetadata | null {
    if (this._lastCreatedWorkflowId === null) {
      return null;
    }
    return {
      event: CocoEvent.WorkflowCreated,
      properties: {
        workflowId: this._lastCreatedWorkflowId,
        chatId: this._lastCreatedChatId,
      },
    };
  }
}
